import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  appendCarePlanGenieSuggestion,
  buildCarePlanGeniePreview,
  buildCarePlanGenieStepDrafts,
  CarePlanGenieFieldDefinition,
  CarePlanGeniePreview,
  CarePlanGenieQuestion,
} from "./carePlanGenieShared";

interface SectionGenieDialogProps {
  visible: boolean;
  title: string;
  questions: CarePlanGenieQuestion[];
  fieldDefinitions: Record<string, CarePlanGenieFieldDefinition>;
  currentValues: Record<string, any>;
  successMessage: string;
  errorMessage: string;
  onHide: () => void;
  onApply: (payload: CarePlanGeniePreview) => Promise<void> | void;
}

const genieAccent = "#475569";
const genieBorder = "#dbe3ef";
const genieTextMuted = "#64748b";
const genieWarmAccent = "#c2410c";
const genieWarmBorder = "#fdba74";
const genieWarmBackground = "#fff7ed";

const SectionGenieDialog: React.FC<SectionGenieDialogProps> = ({
  visible,
  title,
  questions,
  fieldDefinitions,
  currentValues,
  successMessage,
  errorMessage,
  onHide,
  onApply,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string[]>>({});
  const [questionDrafts, setQuestionDrafts] = useState<Record<string, Record<string, string>>>({});
  const [touchedDraftFields, setTouchedDraftFields] = useState<Record<string, string[]>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveNotice, setSaveNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const stackPanels = useResponsive().isMobile;
  const contentRef = useRef<HTMLDivElement | null>(null);

  const currentQuestion = questions[currentStepIndex];
  const currentSelections = selectedOptions[currentQuestion?.id] || [];
  const preview = useMemo(
    () => buildCarePlanGeniePreview(fieldDefinitions, questions, currentValues, selectedOptions, questionDrafts),
    [currentValues, fieldDefinitions, questionDrafts, questions, selectedOptions],
  );
  const sectionOptions = useMemo(
    () => questions.map((question, index) => ({
      label: question.title,
      value: index,
    })),
    [questions],
  );

  const canApply = preview.reviewItems.length > 0;
  const isLastStep = currentStepIndex >= questions.length - 1;
  const groupedOptions = useMemo(() => {
    if (!currentQuestion) {
      return [];
    }

    return currentQuestion.options.reduce<Array<{ label: string; options: typeof currentQuestion.options }>>((groups, option) => {
      const groupLabel = option.groupLabel || "Options";
      const existingGroup = groups.find((group) => group.label === groupLabel);

      if (existingGroup) {
        existingGroup.options.push(option);
        return groups;
      }

      return [...groups, { label: groupLabel, options: [option] }];
    }, []);
  }, [currentQuestion]);

  useEffect(() => {
    if (!currentQuestion) {
      return;
    }

    const touchedFields = touchedDraftFields[currentQuestion.id] || [];
    const generatedDrafts = buildCarePlanGenieStepDrafts(currentQuestion, currentSelections);

    setQuestionDrafts((current) => {
      const currentQuestionDrafts = current[currentQuestion.id] || {};
      const nextQuestionDrafts: Record<string, string> = { ...currentQuestionDrafts };

      currentQuestion.editableFieldKeys.forEach((fieldKey) => {
        if (touchedFields.includes(fieldKey)) {
          return;
        }

        nextQuestionDrafts[fieldKey] = appendCarePlanGenieSuggestion(
          String(currentValues?.[fieldKey] || ""),
          generatedDrafts[fieldKey] || "",
        );
      });

      return {
        ...current,
        [currentQuestion.id]: nextQuestionDrafts,
      };
    });
  }, [currentQuestion, currentSelections, currentValues, touchedDraftFields]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStepIndex, visible]);

  useEffect(() => {
    if (!saveNotice) {
      return;
    }
    const timeout = window.setTimeout(() => {
      setSaveNotice(null);
    }, 3000);
    return () => window.clearTimeout(timeout);
  }, [saveNotice]);

  const resetAndHide = () => {
    setSelectedOptions({});
    setQuestionDrafts({});
    setTouchedDraftFields({});
    setCurrentStepIndex(0);
    setSaving(false);
    setSaveNotice(null);
    onHide();
  };

  const toggleOption = (questionId: string, optionId: string) => {
    setSelectedOptions((current) => {
      const currentQuestionSelections = current[questionId] || [];
      return {
        ...current,
        [questionId]: currentQuestionSelections.includes(optionId)
          ? currentQuestionSelections.filter((item) => item !== optionId)
          : [...currentQuestionSelections, optionId],
      };
    });
  };

  const handleApply = async () => {
    if (!canApply || saving) {
      return;
    }

    setSaving(true);
    setSaveNotice(null);

    try {
      await onApply(preview);
      setSaveNotice({ type: "success", message: successMessage });
    } catch (error) {
      setSaveNotice({
        type: "error",
        message: error instanceof Error ? error.message : errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header={(
        <div className="flex align-items-center justify-content-center gap-3 flex-wrap" style={{ width: "100%" }}>
          <div style={{ fontWeight: 700, color: "#1e293b" }}>{title}</div>
          <div className="flex align-items-center justify-content-center gap-2 flex-wrap">
            <span style={{ fontWeight: 600, color: "#475569" }}>Select section -&gt;</span>
            <Dropdown
              value={currentStepIndex}
              options={sectionOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => {
                setCurrentStepIndex(event.value);
              }}
              placeholder="Jump to section"
              style={{ minWidth: "13rem" }}
            />
          </div>
        </div>
      )}
      visible={visible}
      style={{ width: "min(96vw, 68rem)" }}
      contentStyle={{ background: "#ffffff", borderRadius: "12px", padding: "0.75rem 1rem 0.9rem" }}
      headerStyle={{
        background: "#f8fafc",
        borderBottom: `1px solid ${genieBorder}`,
        color: "#1e293b",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
      }}
      modal
      onHide={resetAndHide}
      footer={(
        <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
          <div style={{ minHeight: "2rem", display: "flex", alignItems: "center" }}>
            {saveNotice ? (
              <div
                style={{
                  padding: "0.45rem 0.7rem",
                  borderRadius: "8px",
                  border: saveNotice.type === "success" ? "1px solid #bbf7d0" : "1px solid #fecaca",
                  background: saveNotice.type === "success" ? "#f0fdf4" : "#fef2f2",
                  color: saveNotice.type === "success" ? "#166534" : "#991b1b",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                }}
              >
                {saveNotice.message}
              </div>
            ) : null}
          </div>
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button type="button" label="Close" severity="secondary" outlined rounded onClick={resetAndHide} />
            {currentStepIndex > 0 ? (
              <Button
                type="button"
                label="Back"
                severity="secondary"
                outlined
                rounded
                onClick={() => {
                  setCurrentStepIndex((current) => Math.max(0, current - 1));
                }}
              />
            ) : null}
            {!isLastStep ? (
              <Button
                type="button"
                label="Next"
                severity="warning"
                outlined
                rounded
                onClick={() => {
                  setCurrentStepIndex((current) => Math.min(questions.length - 1, current + 1));
                }}
              />
            ) : null}
            <Button
              type="button"
              label="Update Care Plan"
              severity="warning"
              rounded
              loading={saving}
              disabled={!canApply}
              onClick={handleApply}
            />
          </div>
        </div>
      )}
    >
      <div ref={contentRef} style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "0.15rem" }}>
        {currentQuestion ? (
          <div style={{ display: "grid", gap: "0.6rem" }}>
            <div
              style={{
                textAlign: "center",
                fontWeight: 800,
                fontSize: "0.95rem",
                color: "#1e293b",
                letterSpacing: "0.01em",
                padding: "0.1rem 0.2rem",
                border: `1px solid ${genieBorder}`,
                borderLeft: "0",
                borderRight: "0",
                justifySelf: "center",
                minWidth: "min(100%, 8rem)",
              }}
            >
              {currentQuestion.title}
            </div>
            <div
              style={{
                display: "grid",
                gap: "0.65rem",
                gridTemplateColumns: currentQuestion.editableFieldKeys.length
                  ? (stackPanels ? "minmax(0, 1fr)" : "minmax(260px, 0.9fr) minmax(340px, 1.1fr)")
                  : "minmax(0, 1fr)",
                alignItems: "start",
              }}
            >
              <div
                style={{
                  padding: "0.8rem 0.85rem",
                  borderRadius: "8px",
                  border: `1px solid ${genieBorder}`,
                  background: "#ffffff",
                }}
              >
                <div style={{ fontWeight: 800, color: genieAccent, marginBottom: "0.2rem", fontSize: "0.9rem" }}>
                  Question {currentStepIndex + 1} of {questions.length}
                </div>
                <div style={{ color: "#3f2b19", marginBottom: "0.25rem", lineHeight: 1.45, fontWeight: 600, fontSize: "0.95rem" }}>
                  {currentQuestion.prompt}
                </div>
                {currentQuestion.helperText ? (
                  <div style={{ color: genieTextMuted, marginBottom: "0.45rem", lineHeight: 1.45, fontSize: "0.88rem" }}>
                    {currentQuestion.helperText}
                  </div>
                ) : null}
                <div
                  style={{
                    color: "#1e293b",
                    marginBottom: "0.55rem",
                    fontSize: "0.86rem",
                    fontWeight: 700,
                    background: "#f8fafc",
                    border: `1px solid ${genieBorder}`,
                    borderRadius: "7px",
                    padding: "0.42rem 0.6rem",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  Choose all that apply.
                </div>
                <div style={{ display: "grid", gap: "0.7rem" }}>
                  {groupedOptions.map((group) => (
                    <div key={group.label} style={{ display: "grid", gap: "0.45rem" }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: genieWarmAccent,
                          fontSize: "0.86rem",
                          paddingBottom: "0.25rem",
                          borderBottom: `1px solid ${genieWarmBorder}`,
                          background: genieWarmBackground,
                          paddingTop: "0.35rem",
                          paddingLeft: "0.5rem",
                          paddingRight: "0.5rem",
                          borderRadius: "6px",
                        }}
                      >
                        {group.label}
                      </div>
                      <div style={{ display: "grid", gap: "0.45rem" }}>
                        {group.options.map((option) => {
                          const selected = currentSelections.includes(option.id);
                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => toggleOption(currentQuestion.id, option.id)}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                borderRadius: "8px",
                                border: selected ? "2px solid #94a3b8" : `1px solid ${genieBorder}`,
                                background: selected ? "#f8fafc" : "#ffffff",
                                padding: "0.6rem 0.7rem",
                                cursor: "pointer",
                                boxShadow: selected ? "0 1px 2px rgba(15, 23, 42, 0.06)" : "none",
                                transition: "all 160ms ease",
                              }}
                              onMouseEnter={(event) => {
                                if (selected) {
                                  return;
                                }
                                event.currentTarget.style.borderColor = genieWarmBorder;
                                event.currentTarget.style.background = genieWarmBackground;
                              }}
                              onMouseLeave={(event) => {
                                if (selected) {
                                  return;
                                }
                                event.currentTarget.style.borderColor = genieBorder;
                                event.currentTarget.style.background = "#ffffff";
                              }}
                            >
                              <div style={{ fontWeight: 800, color: "#0f172a", marginBottom: option.description ? "0.15rem" : 0, fontSize: "0.9rem", lineHeight: 1.35 }}>
                                {option.label}
                              </div>
                              <div style={{ color: genieTextMuted, lineHeight: 1.4, fontSize: "0.84rem" }}>{option.description}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {currentQuestion.editableFieldKeys.length ? (
                <div
                  style={{
                    padding: "0.8rem 0.85rem",
                    borderRadius: "8px",
                    border: `1px solid ${genieBorder}`,
                    background: "#ffffff",
                    minWidth: 0,
                  }}
                >
                  <div style={{ fontWeight: 700, color: genieAccent, marginBottom: "0.4rem", fontSize: "0.9rem" }}>Edit suggested text</div>
                  <div style={{ display: "grid", gap: "0.55rem" }}>
                    {currentQuestion.editableFieldKeys.map((fieldKey) => (
                      <label key={fieldKey} style={{ display: "grid", gap: "0.25rem" }}>
                        <span style={{ fontWeight: 700, color: "#334155", fontSize: "0.84rem" }}>
                          {fieldDefinitions[fieldKey]?.label || fieldKey}
                        </span>
                        <InputTextarea
                          value={questionDrafts[currentQuestion.id]?.[fieldKey] || ""}
                          onChange={(event) => {
                            setTouchedDraftFields((current) => ({
                              ...current,
                              [currentQuestion.id]: Array.from(new Set([...(current[currentQuestion.id] || []), fieldKey])),
                            }));
                            setQuestionDrafts((current) => ({
                              ...current,
                              [currentQuestion.id]: {
                                ...(current[currentQuestion.id] || {}),
                                [fieldKey]: event.target.value,
                              },
                            }));
                          }}
                          rows={fieldKey.toLowerCase().includes("height") || fieldKey.toLowerCase().includes("weight") ? 1 : 6}
                          autoResize
                          className="w-full"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

export default SectionGenieDialog;
